"use client";

import { ReactNode } from "react";
import { ErrorBoundary as ErrorBoundaryComponent } from "react-error-boundary";
import { trpcStandalone } from "~/utils/trpcStandalone";
import { reloadForStaleBundle } from "~/utils/staleBundle";

/**
 * Report a crash that reached the top of the tree. Without this the fallback is
 * the only trace a render failure leaves anywhere — nothing reaches client_log,
 * so a bug that blanks the app can sit in production unnoticed.
 */
const describe = (error: unknown) =>
  error instanceof Error
    ? `${error.name}: ${error.message}\n${error.stack ?? "no stack"}`
    : String(error);

const reportCrash = (error: unknown, componentStack: string | null) => {
  trpcStandalone.clientLog.log
    .mutate({
      message: `Unhandled render error: ${describe(error)}`,
      level: "error",
      url: window.location.href,
      createdAt: new Date().toISOString(),
      meta: { componentStack },
      buildTime:
        (import.meta.env.VITE_BUILD_TIME as string | undefined) ?? "unknown",
    })
    .catch((err: unknown) => {
      console.error("Error logging render error to clientLog", err);
    });
};

export function ErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundaryComponent
      fallback={<div>Something went wrong...</div>}
      onError={(error, info) => {
        console.error("Unhandled render error", error);
        // A tab running a previous deploy's bundle recovers by reloading, and
        // the reload discards this render — nothing worth reporting.
        if (reloadForStaleBundle(error)) return;
        reportCrash(error, info.componentStack ?? null);
      }}
    >
      {children}
    </ErrorBoundaryComponent>
  );
}
